import { useState, type ReactNode } from "react";
import { Moai } from "./Moai.js";
import type { Article, Badge, Profil, Sanction } from "./profil.js";
import type { SkinId } from "./skins.js";
import type { LigneChat } from "./Tribu.js";
import { Barre, Bulle, Ecran, Etape, Feuille, Fleur, Libelle, Parole, Retour, heure, pluriel } from "./ui.js";
import { ESPACES_HUMAINS } from "../espaces.js";

/**
 * Les écrans du lot « compte et soirée » (wireframe « HOA raconte », rangée
 * « Moi ») : badges, boutique, soirée gardée, compte suspendu, sessions
 * (hors session, prévenu), relire une soirée passée, signaler hors tribu,
 * règles et confidentialité.
 */

function BarreRetour(props: { titre?: ReactNode; onRetour: () => void }): JSX.Element {
  return (
    <Barre>
      <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
        <Retour />
      </button>
      {props.titre ? <span className="titre">{props.titre}</span> : null}
    </Barre>
  );
}

/* ------------------------------------------------------------------ */
/* Badges (H-Badges)                                                   */
/* ------------------------------------------------------------------ */

export function Badges(props: { skin: SkinId; badges: Badge[] | null; onRetour: () => void; onCompte: () => void }): JSX.Element {
  const b = props.badges;
  const obtenus = b?.filter((x) => x.obtenu).length ?? 0;
  const prochain = b?.find((x) => !x.obtenu && x.id !== "certifie");
  return (
    <Ecran skin={props.skin} label="Badges">
      <BarreRetour titre={b ? `Badges · ${obtenus} sur ${b.length}` : "Badges"} onRetour={props.onRetour} />
      {b ? (
        <>
          <Parole expression={prochain ? "clin" : "rire"}>{prochain ? `Le prochain : « ${prochain.nom} ». ${prochain.progres ? capitale(prochain.progres) + "." : ""}` : "Tu les as tous. Je n'ai plus rien à t'apprendre."}</Parole>
          <div className="hoa-badges">
            {b.map((x) => (
              <div key={x.id} className="hoa-badge">
                <i className={x.obtenu ? "ok" : "non"} aria-hidden="true">
                  {x.symbole}
                </i>
                <b>{x.nom}</b>
                {!x.obtenu && x.progres ? <span>{x.progres}</span> : null}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <Parole expression="question">Les badges se gardent avec un compte : sans lui, je ne peux pas me souvenir de toi.</Parole>
          <button type="button" className="hoa-go" onClick={props.onCompte}>
            Créer un compte
          </button>
        </>
      )}
    </Ecran>
  );
}

function capitale(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/* ------------------------------------------------------------------ */
/* Boutique (H-Boutique, H-Boutique-Skins)                             */
/* ------------------------------------------------------------------ */

type Rayon = "hoa" | "profil" | "skins" | "musique";

/** Les accessoires de Hoa attendent d'être redessinés sur la nouvelle illustration. */
export const RAYON_HOA_EN_PAUSE = true;

const SKINS_A_VENIR = [
  { nom: "Papier", prix: 150, fond: "#F2F2F2", texte: "#111111", gris: "#BDBDBD" },
  { nom: "Affiche", prix: 200, fond: "#2B2B2B", texte: "#D9D9D9", gris: "#6B6B6B" },
  { nom: "Bande dessinée", prix: 250, fond: "#D9D9D9", texte: "#111111", gris: "#FFFFFF" },
];

export function Boutique(props: {
  skin: SkinId;
  profil: Profil | null;
  envoi: boolean;
  erreur: string | null;
  onPrendre: (a: Article) => void;
  onRetour: () => void;
  onCompte: () => void;
}): JSX.Element {
  const [rayon, setRayon] = useState<Rayon>("hoa");
  const p = props.profil;
  const articles = (p?.articles ?? []).filter((a) => a.rayon === "hoa");
  const [choix, setChoix] = useState<string | null>(null);
  const choisi = articles.find((a) => a.id === (choix ?? p?.porte.hoa)) ?? null;
  const possede = (a: Article) => p?.possessions.includes(a.id) ?? false;
  const porte = (a: Article) => p?.porte.hoa === a.id;

  let bulle: string;
  if (!p) bulle = "La boutique demande un compte : c'est lui qui garde tes points.";
  else if (rayon === "hoa") bulle = "Je change de tenue : mes accessoires reviennent bientôt.";
  else bulle = "Bientôt.";

  const bouton = (() => {
    if (!p) return { texte: "Créer un compte", actif: true, action: props.onCompte };
    // Rayon HOA en pause : les accessoires sont à redessiner sur la nouvelle illustration.
    if (RAYON_HOA_EN_PAUSE || rayon !== "hoa" || !choisi) return { texte: "Bientôt disponible", actif: false, action: () => undefined };
    if (porte(choisi)) return { texte: "Porté", actif: false, action: () => undefined };
    if (possede(choisi)) return { texte: "Le porter", actif: true, action: () => props.onPrendre(choisi) };
    if (p.points < choisi.prix) return { texte: `Il te manque ${choisi.prix - p.points} points`, actif: false, action: () => undefined };
    return { texte: `Prendre · ${choisi.prix} points`, actif: true, action: () => props.onPrendre(choisi) };
  })();

  return (
    <Ecran skin={props.skin} label="Boutique">
      <BarreRetour titre={p ? `Boutique · ${p.points} ${pluriel(p.points, "point", "points")}` : "Boutique"} onRetour={props.onRetour} />
      {bulle ? <Parole expression="clin">{bulle}</Parole> : null}
      <div className="hoa-rayons" role="tablist" aria-label="Rayons">
        {(
          [
            ["hoa", "HOA"],
            ["profil", "Profil"],
            ["skins", "Skins"],
            ["musique", "Musique"],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={rayon === k} className={rayon === k ? "on" : ""} onClick={() => setRayon(k)}>
            {l}
          </button>
        ))}
      </div>
      {rayon === "hoa" && !RAYON_HOA_EN_PAUSE ? (
        <div className="hoa-articles">
          {articles.map((a) => (
            <button key={a.id} type="button" className={choisi?.id === a.id ? "hoa-article on" : "hoa-article"} aria-pressed={choisi?.id === a.id} onClick={() => setChoix(a.id)}>
              <span className="vis">
                <Moai accessoire={a.id === "hoa-classique" ? null : a.id} toujours />
              </span>
              <b>{a.nom}</b>
              <span>{porte(a) ? "Porté" : possede(a) ? "À toi" : a.prix === 0 ? "Offert" : `${a.prix} points`}</span>
            </button>
          ))}
        </div>
      ) : rayon === "skins" ? (
        <div className="hoa-articles">
          {SKINS_A_VENIR.map((s) => (
            <div key={s.nom} className="hoa-article">
              <span className="mini" style={{ background: s.fond }}>
                <em style={{ background: s.texte }} />
                <i style={{ background: s.texte, width: "80%" }} />
                <i style={{ background: s.gris, width: "60%" }} />
                <i style={{ background: s.gris, width: "70%" }} />
              </span>
              <b>{s.nom}</b>
              <span>{s.prix} points</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="hoa-articles" />
      )}
      {props.erreur ? (
        <div className="hoa-erreur" role="alert">
          {props.erreur}
        </div>
      ) : null}
      <button type="button" className="hoa-go" style={{ marginTop: "auto" }} disabled={!bouton.actif || props.envoi} onClick={bouton.action}>
        {bouton.texte}
      </button>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Soirée gardée (H-Garder)                                            */
/* ------------------------------------------------------------------ */

export function Garder(props: {
  skin: SkinId;
  pseudo: string;
  reunis: boolean;
  gain: number | null;
  total: number;
  nouveaux: Badge[];
  avecCompte: boolean;
  /** Fin d'une table d'événement. */
  table?: boolean;
  onTerminer: () => void;
  onCompte: () => void;
}): JSX.Element {
  const nom = props.pseudo ? `, ${props.pseudo}` : "";
  // Espaces Humains : ni points ni compte ; la visite continue sur le salon.
  if (ESPACES_HUMAINS) {
    return (
      <Ecran skin={props.skin} label="Fin de la table">
        <BarreRetour onRetour={props.onTerminer} />
        <div className="hoa-centre">
          <div className="hoa-rang">
            <Moai expression={props.reunis ? "rire" : "neutre"} />
            <Bulle>{props.reunis ? `Merci${nom}. Bonne suite de visite !` : "La table s'arrête là pour toi. Une autre t'attend peut-être."}</Bulle>
          </div>
        </div>
        <button type="button" className="hoa-go" onClick={props.onTerminer}>
          Voir les tables
        </button>
      </Ecran>
    );
  }
  const texte = !props.reunis
    ? "Ta soirée s'arrête là. Je te garde une place pour la prochaine."
    : props.avecCompte
      ? `C'est gardé${nom}. Cette soirée et tes points sont à toi.`
      : props.gain
        ? `Belle soirée${nom}. ${props.gain} points pour toi, gardés sur ce téléphone.`
        : "Belle soirée. Deux rencontres récompensées aujourd'hui, c'est le maximum.";
  return (
    <Ecran skin={props.skin} label="Soirée gardée">
      <BarreRetour onRetour={props.onTerminer} />
      <div className="hoa-centre">
        <div className="hoa-rang">
          <Moai expression={props.reunis ? "rire" : "neutre"} />
          <Bulle>{texte}</Bulle>
        </div>
        {props.reunis && (props.gain || props.nouveaux.length > 0) ? (
          <div className="hoa-carte-gain">
            <b>
              {props.gain ? (
                <>
                  <Fleur taille={20} /> +{props.gain} points
                </>
              ) : null}
              {props.gain && props.nouveaux.length ? " · " : ""}
              {props.nouveaux.length ? `${props.nouveaux.length} ${pluriel(props.nouveaux.length, "badge", "badges")}` : ""}
            </b>
            {props.nouveaux.length ? <span>{props.nouveaux.map((b) => b.nom).join(", ")}</span> : <span>{props.total} points en tout</span>}
          </div>
        ) : null}
      </div>
      <button type="button" className="hoa-go" onClick={props.onTerminer}>
        Terminer
      </button>
      {!props.avecCompte && props.reunis ? (
        <button type="button" className="hoa-lien" onClick={props.onCompte}>
          Garder mes points avec un compte
        </button>
      ) : null}
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Compte suspendu (H-Suspendu)                                        */
/* ------------------------------------------------------------------ */

const dateLongue = (ms: number): string => new Date(ms).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

export function Suspendu(props: {
  skin: SkinId;
  sanction: Sanction;
  envoi: boolean;
  erreur: string | null;
  onContester: (texte: string) => void;
  onRegles: () => void;
  onAccueil: () => void;
}): JSX.Element {
  const [feuille, setFeuille] = useState(false);
  const [texte, setTexte] = useState("");
  const s = props.sanction;
  return (
    <Ecran
      skin={props.skin}
      label="Compte suspendu"
      dessus={
        feuille ? (
          <Feuille label="Contester" onFermer={() => setFeuille(false)}>
            <Parole expression="question">Dis-moi ce qui s'est passé. La régie relit ta réponse et peut lever la décision.</Parole>
            <textarea className="hoa-champ hoa-champ-bloc" rows={4} maxLength={600} aria-label="Ta réponse" value={texte} onChange={(e) => setTexte(e.target.value)} />
            {props.erreur ? (
              <div className="hoa-erreur" role="alert">
                {props.erreur}
              </div>
            ) : null}
            <button
              type="button"
              className="hoa-go"
              disabled={texte.trim().length < 3 || props.envoi}
              onClick={() => {
                props.onContester(texte.trim());
                setFeuille(false);
              }}
            >
              Envoyer
            </button>
          </Feuille>
        ) : undefined
      }
    >
      <BarreRetour onRetour={props.onAccueil} />
      <div className="hoa-centre">
        <div className="hoa-rang">
          <Moai expression="mefiant" />
          <Bulle>
            {s.type === "bannir" ? (
              <>Je ne peux plus te réunir avec d'autres. Ton compte est fermé.</>
            ) : (
              <>
                Je ne peux plus te réunir avec d'autres jusqu'au<big>{dateLongue(s.jusqua ?? Date.now())}</big>
              </>
            )}
            Quelqu'un a signalé ton comportement.
          </Bulle>
        </div>
        <div className="hoa-carte-gain">
          <span>Motif</span>
          <b>{s.motif}</b>
        </div>
        {s.contestee ? <p className="hoa-note">Ta réponse est chez la régie.</p> : null}
      </div>
      {!s.contestee ? (
        <button type="button" className="hoa-go" onClick={() => setFeuille(true)}>
          Contester
        </button>
      ) : null}
      <button type="button" className="hoa-go2" onClick={props.onRegles}>
        Lire les règles de HOA
      </button>
      <button type="button" className="hoa-lien" onClick={props.onAccueil}>
        Mon compte
      </button>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Sessions : hors session (H-HorsSession), prévenu (H-Prevenu)         */
/* ------------------------------------------------------------------ */

export interface Planning {
  toujours: boolean;
  ouvert: boolean;
  enCours: { debut: number; fin: number } | null;
  prochaine: { debut: number; fin: number } | null;
  /** L'avance minimale d'un rendez-vous, en minutes : la fenêtre du chat plus cinq. */
  avanceMin?: number;
}

const jourSession = (ms: number, maintenant: number): string => {
  const j = (x: number) => new Date(x).toDateString();
  if (j(ms) === j(maintenant)) return "Ce soir";
  if (j(ms) === j(maintenant + 86_400_000)) return "Demain";
  const t = new Date(ms).toLocaleDateString("fr-FR", { weekday: "long" });
  return capitale(t);
};

export function libelleSession(ms: number, maintenant: number): string {
  return `${jourSession(ms, maintenant)}, ${heure(ms)}`;
}

/**
 * Les étapes à venir, avant d'avoir choisi : un aperçu, partout le même
 * (proposition, liste, entrée, événement). Une fois dans la tribu, ce sont
 * ses vraies étapes qui prennent la place.
 */
export function EtapesAVenir(props: { table?: boolean }): JSX.Element {
  return (
    <>
      <Etape etat="avenir">
        <Libelle>{props.table ? "Chat de la table" : "Chat de la tribu, 15 min"}</Libelle>
      </Etape>
      <Etape etat="avenir">
        <Libelle>{props.table ? "Rendez-vous à la table" : "Rendez-vous sur place"}</Libelle>
      </Etape>
      <Etape etat="avenir" dernier>
        <Libelle>Réunis</Libelle>
      </Etape>
    </>
  );
}

/** Un fichier d'agenda (.ics) avec un rappel une demi-heure avant. */
export function telechargerAgenda(o: { titre: string; debut: number; fin: number; lieu?: string }): void {
  const f = (ms: number) => new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const lignes = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Hoa//Sessions//FR",
    "BEGIN:VEVENT",
    `UID:${o.debut}-${Math.random().toString(36).slice(2)}@hoa`,
    `DTSTAMP:${f(Date.now())}`,
    `DTSTART:${f(o.debut)}`,
    `DTEND:${f(o.fin)}`,
    `SUMMARY:${o.titre}`,
    ...(o.lieu ? [`LOCATION:${o.lieu}`] : []),
    `URL:${location.origin}/`,
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${o.titre}`,
    "TRIGGER:-PT30M",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ];
  const blob = new Blob([lignes.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "hoa-session.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function Prevenu(props: { skin: SkinId; session: { debut: number; fin: number }; maintenant: number; barre: ReactNode; dessus?: ReactNode; onRetour: () => void }): JSX.Element {
  const rappel = props.session.debut - 30 * 60_000;
  return (
    <Ecran skin={props.skin} label="Prévenu" dessus={props.dessus}>
      {props.barre}
      <div className="hoa-frise">
        <Etape etat="courante" expression="pouce">
          <Bulle>
            C'est noté. Je te réveille<big>{libelleSession(rappel, props.maintenant).replace(",", " à")}</big>une demi-heure avant que les tribus partent.
          </Bulle>
          <button
            type="button"
            className="hoa-lien"
            style={{ marginTop: 12 }}
            onClick={() => telechargerAgenda({ titre: "Hoa : les tribus partent", debut: props.session.debut, fin: props.session.fin })}
          >
            Ajouter à mon agenda
          </button>
          <button type="button" className="hoa-lien" onClick={props.onRetour}>
            Revenir
          </button>
        </Etape>
        <EtapesAVenir />
      </div>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Relire le chat d'une soirée passée                                  */
/* ------------------------------------------------------------------ */

export function RelireSoiree(props: { skin: SkinId; titre: string; lignes: LigneChat[]; moi: string | null; onRetour: () => void }): JSX.Element {
  return (
    <Ecran skin={props.skin} label="Relire le chat">
      <BarreRetour titre={props.titre} onRetour={props.onRetour} />
      <div className="hoa-chat-relu hoa-defile">
        {props.lignes.map((l) =>
          l.hoa ? (
            <div key={l.id} className="hoa-msg-hoa">
              {l.text} <span>{heure(l.at)}</span>
            </div>
          ) : (
          <div key={l.id} className={l.pid === props.moi ? "hoa-msg moi" : "hoa-msg"}>
            <span className="qui">
              {l.pid === props.moi ? "Toi" : l.pseudo} · {heure(l.at)}
            </span>
            <span className="quoi">{l.text}</span>
          </div>
          ),
        )}
      </div>
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */
/* Règles, confidentialité, mentions légales                           */
/* ------------------------------------------------------------------ */

export function Regles(props: { skin: SkinId; texteTransparence?: string; onRetour: () => void }): JSX.Element {
  return (
    <Ecran skin={props.skin} label="Règles, confidentialité, mentions légales">
      <BarreRetour onRetour={props.onRetour} />
      <div className="hoa-defile hoa-texte">
        <h1 className="hoa-grand-titre petit">Les règles de HOA</h1>
        <ol>
          <li>18 ans ou plus.</li>
          <li>Des groupes de 3 à 8, jamais un duo imposé.</li>
          <li>Des lieux publics, choisis à l'avance.</li>
          <li>On peut toujours partir, et dire non.</li>
          <li>Pas de propos ni d'insistance déplacés : on signale, la régie décide (avertissement, suspension de 7 jours, fermeture).</li>
        </ol>

        <h2>Ce qui est gardé</h2>
        <p>{props.texteTransparence ?? "Vos réponses servent uniquement à cette rencontre et disparaissent avec elle."}</p>
        <ul>
          <li>
            <b>Pendant une tribu de la ville</b> : pseudo, réponses, messages, position au rendez-vous. À la clôture, tout est effacé ; restent des
            compteurs sans nom.
          </li>
          <li>
            <b>Votre position</b> sert à trouver les tribus et à savoir si vous êtes arrivé. Elle n'est montrée à personne et n'est pas gardée.
          </li>
          <li>
            <b>Avec un compte</b> : e-mail, pseudo, empreinte du mot de passe (jamais le mot de passe), vos points, un résumé de vos soirées (sujet,
            lieu, heure, nombre de présents), vos badges et ce que vous avez pris à la boutique. Rien de ce que vous avez écrit ou répondu.
          </li>
          <li>
            <b>Dans un événement</b> (séminaire, salon…), l'organisateur garde les échanges pour son bilan, pendant la durée qu'il annonce à
            l'entrée. C'est lui qui en répond.
          </li>
          <li>
            <b>Votre photo de profil</b>, si vous en mettez une, est vue par les membres de vos tribus. Avec un compte, le serveur la garde avec
            lui ; la régie peut la retirer si elle est signalée.
          </li>
          <li>
            <b>Qui était dans une tribu</b> (pseudo, lettre, compte), sans messages ni réponses, est gardé sept jours après la soirée, pour qu'on
            puisse signaler quelqu'un après coup. Puis c'est effacé.
          </li>
          <li>
            <b>Un signalement</b> garde le seul message visé (ou la photo), jusqu'à la décision de la régie.
          </li>
          <li>
            <b>Sur votre téléphone</b> seulement : l'historique détaillé de vos soirées (avec qui, point commun, chat relu).
          </li>
        </ul>
        <h2>Vos droits</h2>
        <p>
          Paramètres › Mon compte : télécharger vos données, supprimer votre compte (tout est effacé). Pour le reste, écrivez à l'éditeur ci-dessous.
        </p>

        <h2>Mentions légales</h2>
        <p>
          Éditeur : MATV (adresse et contact à compléter). Hébergement : serveur de l'éditeur, relié à Internet par Cloudflare (Cloudflare, Inc., 101 Townsend St, San Francisco, CA
          94107, États-Unis).
        </p>

        <h2>Crédits de la carte</h2>
        <p>
          Fond de carte : © les contributeurs d'OpenStreetMap, sous licence ODbL ; tuiles OpenFreeMap © OpenMapTiles. Itinéraires à pied : OSRM,
          serveur de FOSSGIS.{" "}
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
            Licence OpenStreetMap
          </a>
        </p>

        <h2>D'où vient HOA</h2>
        <p>
          HOA est un moaï. Sur l'île de Rapa Nui, les moaï sont des ancêtres : <em>aringa ora o te tupuna</em>, « le visage vivant des ancêtres ».
          L'un d'eux, <em>Hoa Hakananai'a</em> — « l'ami perdu ou volé » — est au British Museum depuis 1869 ; en 2018, la gouverneure de l'île en
          a demandé le retour. <em>Hoa</em>, seul, se rend d'ordinaire par « ami ». Ces mots appartiennent à un peuple vivant ; nous les empruntons
          en le disant.
        </p>
      </div>
    </Ecran>
  );
}
